import React from 'react';
import * as THREE from 'three';
import { cadToThreeJsPositionWithOffset } from '../../Scenes/scene-utils';
import { CAD_TO_THREEJS_MATRIX, CadOrigin, parseGeometryNearOrigin } from './geometryLoader';

interface ImportedSolidMeshProps {
  objData: string | undefined;
  guid: string | undefined;
  groupPosition: THREE.Vector3;
  groupScale: number;
  color?: number;
  opacity?: number;
  sourceName?: string;
}

interface BuiltSolid {
  mesh: THREE.Mesh;
  origin: CadOrigin;
}

const DEFAULT_IMPORTED_SOLID_COLOR = 0x4488ff;
const DEFAULT_IMPORTED_SOLID_OPACITY = 0.3;

const disposeSolid = (solid: BuiltSolid | null) => {
  if (!solid) return;
  solid.mesh.geometry.dispose();
  const material = solid.mesh.material as THREE.Material | THREE.Material[] | undefined;
  if (Array.isArray(material)) {
    material.forEach((m) => m.dispose());
  } else {
    material?.dispose?.();
  }
};

const buildSolid = (objData: string, sourceName?: string): BuiltSolid | null => {
  const parsed = parseGeometryNearOrigin(objData, sourceName ?? 'solid.obj');
  if (!parsed) return null;
  const { geometry, origin } = parsed;
  geometry.applyMatrix4(CAD_TO_THREEJS_MATRIX);
  geometry.computeBoundingBox();
  geometry.computeVertexNormals();
  return { mesh: new THREE.Mesh(geometry), origin };
};

const ImportedSolidMesh = ({
  objData,
  guid,
  groupPosition,
  groupScale,
  color,
  opacity = DEFAULT_IMPORTED_SOLID_OPACITY,
  sourceName,
}: ImportedSolidMeshProps) => {
  const [solid, setSolid] = React.useState<BuiltSolid | null>(null);
  const lastGuidRef = React.useRef<string | undefined>();

  // Same transform as the camera framing and the clipping box: (cad + offset) * scale.
  const position = React.useMemo(
    () => (solid ? new THREE.Vector3(...cadToThreeJsPositionWithOffset(solid.origin, groupPosition, groupScale)) : undefined),
    [solid, groupPosition, groupScale],
  );

  React.useEffect(() => {
    if (!objData) {
      setSolid((prev) => {
        disposeSolid(prev);
        return null;
      });
      lastGuidRef.current = undefined;
      return;
    }

    if (guid === lastGuidRef.current) return;
    lastGuidRef.current = guid;

    setSolid((prev) => {
      disposeSolid(prev);
      return null;
    });

    setSolid(buildSolid(objData, sourceName));
  }, [objData, guid, sourceName]);

  React.useEffect(() => () => {
    disposeSolid(solid);
  }, [solid]);

  if (!solid) return null;

  return (
    <group key={guid ?? 'solid'} position={position} scale={groupScale}>
      <primitive object={solid.mesh}>
        <meshPhongMaterial
          attach="material"
          color={color ?? DEFAULT_IMPORTED_SOLID_COLOR}
          transparent={opacity < 1}
          opacity={opacity}
          side={THREE.DoubleSide}
          depthWrite={opacity >= 1}
        />
      </primitive>
    </group>
  );
};

export default ImportedSolidMesh;
